import {Language} from 'yougile-lang'
import {Util} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  HubLike,
  RegistrationType,
  Tag,
  UserUITheme,
  UserUIThemeVariation,
  UserUIThemeVariationPerTheme
} from 'yougile-model'
import {Items} from '@api/model/items'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'

interface ProjectStats {
  tasks: number
  messages: number
  newMessages: number
  files: number
  latestActivity: number // timestamp
}

export interface Globals {
  allowUsersToInvite: boolean
  currentUserId: DataObjectId
  taskToConnect: Hub | undefined
  taskToConnectType: 'connection' | 'dependency' | 'dependent' | undefined
  // TODO deprecate this function, make props observable on migrate to react
  taskToConnectUpdated?: () => void

  // TODO move it to logged-in-store
  scriptsOpen: boolean
  // TODO deprecate this function, make props observable on migrate to react
  scriptsOpenUpdated?: () => void
  companyChanging: boolean
  requestOpenStickerOnTask: [Tag, Hub] // sticker, task
  boardsAutoFiltered: Set<DataObjectId>
  /**
   * Calculated and updated in leftPanel, used everywhere.
   */
  projectStats: Map<DataObjectId, ProjectStats>
  openedChatNoScroll: boolean

  // enables only explicit writes to the database, useful for debugging
  silentMode: boolean
  readonly: boolean
  standalone: boolean
  devMode: boolean
  noUsers: boolean
  noLicense: boolean
  licenseExpired: boolean
  phone: string
  registrationType: RegistrationType
  openStats(): void
  closeStats(): void
  getCompanyId(): DataObjectId
  getHubLike(id: DataObjectId): HubLike
  setCompanyId(companyId: DataObjectId | undefined, noListeners?: boolean): void
  onCompanyChange(listener: () => void): number
  removeCompanyListener(id: number | (() => void)): void
  dragAllowed(id: DataObjectId): boolean

  disableInviteUsers?: boolean
  disableEmailRecovery?: boolean
  allowExtensions?: boolean
  supportSyncRoleUsers?: boolean
  allowUploadExtensions?: boolean
  allowWebExtensions?: boolean
  webPlatformUrl?: string
  mainCompanyId?: DataObjectId
  lang?: Language
  offline: boolean
  theme: UserUITheme
  setTheme: (theme: UserUITheme) => void
  themeVariation: UserUIThemeVariationPerTheme
  setThemeVariation: (
    theme: UserUITheme,
    variation: UserUIThemeVariation
  ) => void
  columnColorEnabled: boolean
  setColumnColorEnabled: (value: boolean) => void
  sharedId: string
  desktopVersion?: string
  googleOAuthClientIdDev: string
  googleOAuthClientIdTest: string
  googleOAuthClientIdProd: string
  initEnabledExtensions: {[companyId: string]: string[]}
}

const loc = {
  companyId: undefined as DataObjectId,

  // first element to prevent 0 converted listener removements
  listeners: [undefined] as Array<() => void>
}

const loadedTheme: UserUITheme =
  Storage.getItem('theme') === 'dark' ? 'dark' : 'light'

const themeVariationKey = 'theme-variation'

const loadedThemeVariation: UserUIThemeVariationPerTheme = (() => {
  const data =
    Storage.getItemAsJson<UserUIThemeVariationPerTheme>(themeVariationKey)
  if (data) {
    return data
  }

  const defaultData: UserUIThemeVariationPerTheme = {
    light: 'auto',
    dark: 'auto'
  }
  Storage.setItem(themeVariationKey, JSON.stringify(defaultData))
  return defaultData
})()

export const notificationSettingsKey = 'notification-settings'

const columnColorEnabledKey = 'column-color-enabled'

// Enabled by default
const loadedColumnColorEnabled: boolean =
  Storage.getItem(columnColorEnabledKey) !== '0'

export const globals: Globals = {
  allowUsersToInvite: true,
  currentUserId: undefined,
  scriptsOpen: false,
  companyChanging: false,
  taskToConnect: undefined,
  taskToConnectType: undefined,
  requestOpenStickerOnTask: undefined,
  openedChatNoScroll: false,
  boardsAutoFiltered: new Set(),
  projectStats: new Map(),
  silentMode: false,
  readonly: false,
  standalone: false,
  devMode: false,
  noUsers: false,
  noLicense: false,
  licenseExpired: false,
  allowExtensions: false,
  supportSyncRoleUsers: undefined,
  openStats: Util.noop,
  closeStats: Util.noop,
  theme: loadedTheme,
  setTheme: theme => {
    globals.theme = theme
    Storage.setItem('theme', theme)
  },
  themeVariation: loadedThemeVariation,
  setThemeVariation: (theme, variation) => {
    globals.themeVariation = {...globals.themeVariation, [theme]: variation}
    Storage.setItem(themeVariationKey, JSON.stringify(globals.themeVariation))
  },
  columnColorEnabled: loadedColumnColorEnabled,
  setColumnColorEnabled: value => {
    globals.columnColorEnabled = value
    Storage.setItem(columnColorEnabledKey, value ? '1' : '0')
  },
  phone: '',
  registrationType: 'email',
  getCompanyId(): DataObjectId {
    return loc.companyId
  },
  getHubLike(id: DataObjectId): HubLike {
    return {id, companyId: globals.getCompanyId()}
  },
  setCompanyId(companyId: DataObjectId, noListeners = false): void {
    loc.companyId = companyId
    if (!noListeners) {
      loc.listeners.forEach((l: () => void | undefined) => {
        l?.()
      })
    }
  },
  onCompanyChange(listener: () => void): number {
    return loc.listeners.push(listener) - 1
  },
  removeCompanyListener(idOrListener: number | (() => void)): void {
    if (idOrListener === undefined) {
      return
    }
    const id =
      typeof idOrListener === 'number'
        ? idOrListener
        : loc.listeners.findIndex(l => l === idOrListener)

    loc.listeners[id] = undefined
  },
  offline: false,
  sharedId: SharedBoardsClient.getId(),
  dragAllowed: (id: DataObjectId) => {
    const box = Sync.getCurrentSandbox()
    return (
      Items.allowDrag(Items.get(id)) &&
      PermissionsChecker.checkStartMove(box && (box.get(id) as Hub))
    )
  },
  desktopVersion: Util.isDesktop()
    ? Util.catch(() =>
        (location.search ?? '').includes('version=')
          ? location.search.split('version=')[1].split(/[&#]/)[0]
          : undefined
      ) ?? 'error'
    : undefined,
  googleOAuthClientIdDev:
    '697356174218-5388s6a25d5up9fkhc3ok25qnfgpu5dp.apps.googleusercontent.com',
  googleOAuthClientIdTest:
    '697356174218-l5c7lcegln9o7m1n99jbvjjvlokvp1ce.apps.googleusercontent.com',
  googleOAuthClientIdProd:
    '697356174218-8v2jv0v90dh4djn4obcnqd216sh0tvlm.apps.googleusercontent.com',
  initEnabledExtensions: {}
}
